import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, PartyPopper, Sparkles, Film, Play, Star, ArrowRight, Quote, Upload, Music, Scissors, Eye, Download } from 'lucide-react';
import { services, sampleVideos, testimonials as fallbackTestimonials } from '../utils/mockData';
import axios from 'axios';
import Reveal from '../components/Reveal';
import HeroVideoMontage from '../components/HeroVideoMontage';
import TrustBar from '../components/TrustBar';
import MeetTheCreator from '../components/MeetTheCreator';
import StatsSection from '../components/StatsSection';
import HomeFAQ from '../components/HomeFAQ';
import SlimTrustBanner from '../components/SlimTrustBanner';
import FeaturedFilmCard from '../components/FeaturedFilmCard';
import PackagePreview from '../components/PackagePreview';
import PersonalisedSongPromo from '../components/PersonalisedSongPromo';
import { VideoModal } from '../components/VideoPlayer';

const API = process.env.REACT_APP_BACKEND_URL;

// Elegant, gold line-icon timeline of the 5-step process.
const STEPS = [
  { icon: Upload,   title: 'Upload Your Photos & Videos', desc: 'Send us your memories from any device via secure link or QR code.' },
  { icon: Music,    title: 'Choose Your Music',           desc: 'Pick your own tracks - or let us curate the perfect soundtrack.' },
  { icon: Scissors, title: 'We Professionally Edit',       desc: 'Every frame hand-crafted with cinematic pacing and warmth.' },
  { icon: Eye,      title: 'Receive Your Preview',         desc: 'Approve your film - request tweaks until it feels perfect.' },
  { icon: Download, title: 'Receive Your 4K Film',        desc: "Delivered via a secure private Google Drive link plus a physical USB copy - included in every order." },
];

const HomePage = () => {
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [activeVideo, setActiveVideo] = useState(null);

  // Curated Featured Films - 4 emotive story-style cards shown directly below the hero.
  // Titles/descriptions are deliberately personal (not technical) to build emotional buy-in.
  const featuredFilms = [
    {
      category: 'wedding',
      categoryLabel: 'Wedding',
      title: "A Wedding to Remember",
      story: "Every glance, every laugh, every dance - woven into one cinematic love story.",
      sourceId: 10,
    },
    {
      category: 'funeral',
      categoryLabel: 'Funeral Tribute',
      title: "In Loving Memory",
      story: "A heartfelt tribute celebrating a beautiful, unforgettable life.",
      sourceId: 2,
    },
    {
      category: 'birthday',
      categoryLabel: 'Birthday Celebration',
      title: "A Century of Memories",
      story: "A joyful milestone birthday, edited into a cinematic memory to treasure.",
      sourceId: 3,
    },
    {
      category: 'anniversary',
      categoryLabel: 'Anniversary',
      title: "Together, Through the Years",
      story: "A cinematic love letter marking a lifetime of shared moments.",
      sourceId: 8,
    },
    {
      category: 'business',
      categoryLabel: 'Business Promotion',
      title: "Paige's Buttercream Boutique",
      story: "A bespoke promotional presentation created by Visual Creationz, showcasing Paige's beautiful celebration cakes, cupcakes, wedding cakes and personalised creations.",
      credit: 'Created by Visual Creationz.',
      sourceId: 101,
      wide: true,
    },
  ]
    .map((f) => {
      const src = sampleVideos.find((v) => v.id === f.sourceId);
      return src ? { ...f, thumbnail: src.thumbnail, videoUrl: src.videoUrl, mobileVideoUrl: src.mobileVideoUrl, description: src.description } : null;
    })
    .filter(Boolean);

  useEffect(() => {
    axios
      .get(`${API}/api/testimonials`)
      .then((res) => {
        if (res.data?.testimonials?.length) {
          setTestimonials(res.data.testimonials);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-black">
      {/* ═══════════════════════════════════════════════════════════
          1. HERO
          ═══════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <HeroVideoMontage />

        <div className="relative z-10 container mx-auto px-4 lg:px-8 text-center pt-24 md:pt-40">
          <div className="max-w-4xl mx-auto space-y-8">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 leading-tight animate-fade-in mb-4">
              Moments. Made Cinematic.
            </h1>
            <p className="text-lg md:text-2xl text-gray-200 leading-relaxed animate-fade-in-delay max-w-3xl mx-auto">
              Professional cinematic memory films created from your photos, videos and music - for weddings, memorials, birthdays, anniversaries and life's most meaningful moments.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 animate-fade-in-delay-2">
              <Link
                to="/pricing"
                data-testid="hero-create-film-btn"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-xl hover:shadow-amber-500/50 hover:scale-105 active:scale-95"
              >
                Create My Film
                <ArrowRight size={18} className="ml-2" />
              </Link>
              <Link
                to="/samples"
                data-testid="hero-view-portfolio-btn"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/40 text-white font-semibold rounded-full hover:bg-white hover:text-black transition-all duration-300 active:scale-95 group backdrop-blur-sm"
              >
                <Play size={18} className="mr-2 group-hover:animate-pulse" />
                View Our Portfolio
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator removed */}
      </section>

      {/* ═══════════════════════════════════════════════════════════
          2. FEATURED FILMS (moved directly below hero)
          ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-24 bg-black" data-testid="featured-films">
        <div className="container mx-auto px-4 lg:px-8">
          <Reveal className="text-center mb-14 max-w-3xl mx-auto">
            <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-4">Portfolio</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Featured <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Films</span>
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed">
              See how we transform life's most meaningful moments into cinematic memories.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto mb-12">
            {featuredFilms.map((film, idx) => (
              <Reveal key={film.category} delay={Math.min(idx + 1, 4)} className={film.wide ? 'md:col-span-2' : ''}>
                <FeaturedFilmCard
                  film={film}
                  onPlay={(f) => setActiveVideo({ title: f.title, description: f.story, credit: f.credit, videoUrl: f.videoUrl, mobileVideoUrl: f.mobileVideoUrl, thumbnail: f.thumbnail })}
                />
              </Reveal>
            ))}
          </div>

          <Reveal className="text-center">
            <Link
              to="/samples"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-xl hover:shadow-amber-500/50 hover:scale-105 active:scale-95"
              data-testid="portfolio-view-all-btn"
            >
              View Full Portfolio
              <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          3. TRUST BAR
          ═══════════════════════════════════════════════════════════ */}
      <TrustBar />

      {/* ═══════════════════════════════════════════════════════════
          4. HOW IT WORKS  (5-step gold-line timeline)
          ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-gradient-to-b from-black via-gray-900 to-black" data-testid="how-it-works">
        <div className="container mx-auto px-4 lg:px-8">
          <Reveal className="text-center mb-16 max-w-3xl mx-auto">
            <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-4">The Process</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              How It <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Works</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Five simple steps from your first upload to your finished cinematic film.
            </p>
          </Reveal>

          <div className="relative max-w-6xl mx-auto">
            {/* Connector line - desktop */}
            <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-6">
              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <Reveal key={step.title} delay={Math.min(idx + 1, 4)} className="relative text-center group">
                    <div className="mx-auto w-20 h-20 rounded-full border border-amber-500/40 bg-black flex items-center justify-center relative z-10 group-hover:border-amber-400 group-hover:scale-105 transition-all duration-500">
                      <Icon size={28} className="text-amber-400" strokeWidth={1.5} />
                    </div>
                    <div className="mt-5 text-xs uppercase tracking-[0.3em] text-amber-500/70">
                      Step {idx + 1}
                    </div>
                    <h3 className="mt-2 text-white font-semibold text-lg leading-snug">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-gray-400 text-sm leading-relaxed">
                      {step.desc}
                    </p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          5. SERVICES
          ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-black" data-testid="services-overview">
        <div className="container mx-auto px-4 lg:px-8">
          <Reveal className="text-center mb-16 max-w-3xl mx-auto">
            <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-4">Our Services</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Cinematic films, <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">beautifully crafted</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Professional visual storytelling for every kind of occasion.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const icons = { Heart, PartyPopper, Sparkles, Film };
              const Icon = icons[service.icon];
              return (
                <Reveal
                  key={service.id}
                  delay={Math.min(index + 1, 4)}
                  className="group relative bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-8 rounded-2xl border border-white/10 hover:border-amber-500/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-500/10"
                >
                  <div className="w-14 h-14 rounded-full border border-amber-500/40 bg-black flex items-center justify-center mb-6 group-hover:border-amber-400 group-hover:scale-105 transition-all duration-500">
                    <Icon size={24} className="text-amber-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-amber-300 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed text-[15px]">
                    {service.description}
                  </p>
                  <Link
                    to="/services"
                    className="inline-flex items-center text-amber-400 hover:text-amber-300 font-medium transition-colors group/link text-sm"
                  >
                    Learn More
                    <ArrowRight size={16} className="ml-2 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          6. WHY CHOOSE (Meet the Creator)
          ═══════════════════════════════════════════════════════════ */}
      <MeetTheCreator />

      {/* ═══════════════════════════════════════════════════════════
          7. STATS
          ═══════════════════════════════════════════════════════════ */}
      <StatsSection />

      {/* ═══════════════════════════════════════════════════════════
          7b. PACKAGE PREVIEW (compact 3-tier snapshot)
          ═══════════════════════════════════════════════════════════ */}
      <PackagePreview />

      {/* 7c. PERSONALISED SONG UPGRADE */}
      <PersonalisedSongPromo testId="home-song-promo" />

      {/* ═══════════════════════════════════════════════════════════
          8. TESTIMONIALS
          ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900" data-testid="testimonials-section">
        <div className="container mx-auto px-4 lg:px-8">
          <Reveal className="text-center mb-16 max-w-3xl mx-auto">
            <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-4">Client Stories</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Trusted with <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">precious memories</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Real words from real families we've had the privilege of working with.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <Reveal
                key={testimonial.id}
                delay={Math.min(index + 1, 4)}
                className="bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-8 rounded-2xl border border-white/10 hover:border-amber-500/40 transition-all duration-500"
              >
                <Quote size={24} className="text-amber-500/60 mb-4" />
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} size={16} className="text-amber-400" fill="currentColor" />
                  ))}
                </div>
                <p className="text-gray-200 mb-6 leading-relaxed italic text-[15px]">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center space-x-4 pt-4 border-t border-white/5">
                  {testimonial.image && (
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      loading="lazy"
                      className="w-11 h-11 rounded-full object-cover border border-amber-500/40"
                    />
                  )}
                  <div>
                    <p className="font-semibold text-white text-sm">{testimonial.name}</p>
                    <p className="text-xs text-gray-500 uppercase tracking-widest mt-0.5">{testimonial.occasion}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          9. FAQ
          ═══════════════════════════════════════════════════════════ */}
      <HomeFAQ />

      {/* ═══════════════════════════════════════════════════════════
          10. SLIM TRUST BANNER (above final CTA for pre-purchase reassurance)
          ═══════════════════════════════════════════════════════════ */}
      <SlimTrustBanner />

      {/* ═══════════════════════════════════════════════════════════
          11. FINAL CTA
          ═══════════════════════════════════════════════════════════ */}
      <section className="relative py-28 overflow-hidden bg-black" data-testid="final-cta">
        {/* Soft gold glow */}
        <div
          className="absolute inset-0 opacity-70 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 50%, rgba(201, 162, 39, 0.18), transparent 60%)',
          }}
        />

        <div className="container mx-auto px-4 lg:px-8 relative z-10 text-center">
          <Reveal className="max-w-3xl mx-auto space-y-8">
            <p className="uppercase tracking-[0.3em] text-xs text-amber-400">Ready When You Are</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Ready to turn your memories into<br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600"> something unforgettable?</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed italic">
              "Whether it's a wedding, memorial, birthday or celebration, we'll create a cinematic film you'll treasure forever."
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link
                to="/pricing"
                data-testid="final-cta-start-project-btn"
                className="inline-flex items-center justify-center px-9 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-2xl hover:shadow-amber-500/50 hover:scale-105 active:scale-95 w-full sm:w-auto"
              >
                Start Your Project
                <ArrowRight size={18} className="ml-2" />
              </Link>
              <Link
                to="/pricing"
                data-testid="final-cta-view-packages-btn"
                className="inline-flex items-center justify-center px-9 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white hover:text-black transition-all duration-300 w-full sm:w-auto"
              >
                View Packages
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Video Modal (portfolio lightbox) */}
      {activeVideo && <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />}
    </div>
  );
};

export default HomePage;
